import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  CircularProgress,
  Popper,
  ClickAwayListener,
  alpha,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface SearchResult {
  id: string;
  type: string;
  title: string;
  description?: string;
  url: string;
  relevance: number;
}

// Quick navigation items - always available for local search
const quickNavItems: SearchResult[] = [
  { id: 'nav-dashboard', type: 'page', title: 'Dashboard', description: 'Main dashboard with overview metrics', url: '/dashboard', relevance: 100 },
  { id: 'nav-company-profile', type: 'settings', title: 'Company Profile', description: 'Edit your company onboarding information, suppliers, products, and preferences', url: '/company-profile', relevance: 100 },
  { id: 'nav-onboarding', type: 'settings', title: 'Onboarding Settings', description: 'Update business details entered during onboarding - access via Company Profile', url: '/company-profile', relevance: 100 },
  { id: 'nav-settings', type: 'settings', title: 'Settings', description: 'User preferences and notifications', url: '/settings', relevance: 100 },
  { id: 'nav-products', type: 'page', title: 'Products', description: 'Manage your product catalog', url: '/supply-chain/products', relevance: 90 },
  { id: 'nav-suppliers', type: 'page', title: 'Suppliers', description: 'Manage supplier relationships', url: '/supply-chain/suppliers', relevance: 90 },
  { id: 'nav-inventory', type: 'page', title: 'Inventory', description: 'Track stock levels and movements', url: '/supply-chain/inventory', relevance: 90 },
  { id: 'nav-negotiations', type: 'page', title: 'Negotiations', description: 'AI-powered supplier negotiations', url: '/negotiations', relevance: 85 },
  { id: 'nav-agents', type: 'page', title: 'AI Agents', description: 'Configure and monitor AI agents', url: '/agents', relevance: 85 },
  { id: 'nav-analytics', type: 'page', title: 'Analytics', description: 'Supply chain analytics and insights', url: '/analytics', relevance: 80 },
  { id: 'nav-simulations', type: 'page', title: 'Simulations', description: 'Run what-if scenarios', url: '/simulations', relevance: 80 },
  { id: 'nav-integrations', type: 'page', title: 'Integrations', description: 'Connect third-party systems', url: '/integrations', relevance: 75 },
  { id: 'nav-data-import', type: 'page', title: 'Data Import', description: 'Import data from CSV/Excel files', url: '/data-import', relevance: 75 },
  { id: 'nav-help', type: 'page', title: 'Help & FAQ', description: 'Documentation and frequently asked questions', url: '/help', relevance: 70 },
];

const GlobalSearch: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  // Search function with debounce
  const performSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();
    
    // First, filter local quick nav items
    const localResults = quickNavItems.filter(item => 
      item.title.toLowerCase().includes(lowerQuery) ||
      item.description?.toLowerCase().includes(lowerQuery)
    );

    // Show local results immediately
    setResults(localResults);
    if (localResults.length > 0) {
      setOpen(true);
    }

    try {
      setLoading(true);
      const response = await api.search.query(searchQuery, undefined, 10);
      // Combine with local results, avoiding duplicates
      const apiResults = response.data.results || [];
      const combinedResults = [...localResults];
      apiResults.forEach((result: SearchResult) => {
        if (!combinedResults.find(r => r.url === result.url)) {
          combinedResults.push(result);
        }
      });
      setResults(combinedResults);
      setOpen(true);
    } catch (error: any) {
      // Silently handle 404 - search endpoint not implemented yet
      // Keep local results visible
      if (error?.response?.status !== 404) {
        console.error('Search error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for search
    if (value.trim()) {
      searchTimeout.current = setTimeout(() => {
        performSearch(value);
      }, 300); // 300ms debounce
    } else {
      setResults([]);
      setOpen(false);
    }
  };

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to focus search
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        anchorRef.current?.querySelector('input')?.focus();
      }
      // Escape to close
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      product: theme.palette.primary.main,
      supplier: theme.palette.secondary.main,
      inventory: theme.palette.warning.main,
      location: theme.palette.info.main,
      simulation: theme.palette.success.main,
      task: theme.palette.error.main,
      page: theme.palette.info.main,
      settings: theme.palette.secondary.main,
    };
    return colors[type] || theme.palette.grey[500];
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'relative', width: '100%', maxWidth: 400 }} ref={anchorRef}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search... (⌘K)"
          value={query}
          onChange={handleInputChange}
          onFocus={() => query && setOpen(true)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchIcon />
                )}
              </InputAdornment>
            ),
            sx: {
              bgcolor: alpha(theme.palette.common.white, 0.15),
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.25),
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.common.white, 0.3),
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        <Popper
          open={open && results.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ width: anchorRef.current?.offsetWidth, zIndex: 1300 }}
        >
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              maxHeight: 400,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <List disablePadding>
              {results.map((result, index) => (
                <ListItem key={`${result.type}-${result.id}-${index}`} disablePadding>
                  <ListItemButton onClick={() => handleResultClick(result)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {result.title}
                          </Typography>
                          <Chip
                            label={result.type}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              bgcolor: alpha(getTypeColor(result.type), 0.1),
                              color: getTypeColor(result.type),
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      }
                      secondary={result.description}
                      secondaryTypographyProps={{
                        sx: {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            {results.length === 0 && query && !loading && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No results found for "{query}"
                </Typography>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
